import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge } from '../../components/ui/badge';
import { DateRangePicker } from '../../components/ui/DateRangePicker';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { MultiSelectFilter } from '../../components/ui/MultiSelectFilter';
import { SelectField } from '../../components/ui/SelectField';
import { Spinner } from '../../components/ui/Spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { formatExactTimestamp } from '../../lib/inboxView';
import { UNASSIGNED_REASON_LABELS } from '../../lib/leadRelay/assignment';
import {
  DEFAULT_LOG_STATUSES,
  LEAD_SUBMISSION_STATUSES,
  failureLabel,
  STATUS_LABELS,
  willSubmitDespiteTestMode,
} from '../../lib/leadRelay/logView';
import { destinationHeading, systemName } from '../../lib/leadRelay/providerVocabulary';
import type { LeadProvider, LeadSubmissionKind, LeadSubmissionStatus } from '../../types/leadRelay';
import type { RelayOutcome } from './api';
import { LeadSubmissionPanel, statusBadgeVariant } from './LeadSubmissionPanel';
import { useCurrentUser } from '../auth';
import {
  assignLead,
  getLeadRelayStatus,
  getLeadSubmission,
  listLeadLog,
  listLeadRouteNames,
  editLeadInput,
  markLeadKeyedManually,
  resendLead,
} from './api';

// The Lead Log (docs/lead-relay.md §10.2) — every lead the relay received,
// newest first. Open to any signed-in staff: when VirtualYard refuses us, this
// is where the backlog gets keyed in by hand.
//
// Filters live in the URL so a link to "Liverpool's failures this week" can be
// shared, and `?submission=<id>` opens one lead (the alert links land here).

const ALL_STATUSES: LeadSubmissionStatus[] = [...LEAD_SUBMISSION_STATUSES];

/** YYYY-MM-DD (local) → the instant that day starts / ends, locally. */
export function dayBoundsIso(day: string | undefined, edge: 'start' | 'end'): string | undefined {
  if (!day) return undefined;
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  const date = edge === 'start' ? new Date(y, m - 1, d, 0, 0, 0, 0) : new Date(y, m - 1, d, 23, 59, 59, 999);
  return date.toISOString();
}

export function LeadLogPage() {
  // ⚠️ Assign and send is ADMIN-ONLY (the server refuses it too); anyone can read.
  const isAdmin = useCurrentUser().role === 'admin';
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();

  const routeId = params.get('store') ?? '';
  const kind = (params.get('kind') ?? 'live') as LeadSubmissionKind | 'all';
  const statusParam = params.get('status');
  // No param = the default set (duplicates hidden); `all` = every status, which
  // is what an emptied multi-select means (its own "All" convention).
  const statuses =
    statusParam === null
      ? [...DEFAULT_LOG_STATUSES]
      : statusParam === 'all'
        ? [...ALL_STATUSES]
        : statusParam.split(',').filter(Boolean);
  const fromDay = params.get('from') ?? undefined;
  const toDay = params.get('to') ?? undefined;
  const selectedId = params.get('submission');

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  const { data: status } = useQuery({ queryKey: ['lead-relay-status'], queryFn: getLeadRelayStatus });
  const { data: routes = [] } = useQuery({ queryKey: ['lead-route-names'], queryFn: listLeadRouteNames });
  const log = useQuery({
    queryKey: ['lead-log', routeId, kind, statuses.join(','), fromDay, toDay],
    queryFn: () =>
      listLeadLog({
        routeId: routeId || undefined,
        statuses: statuses as LeadSubmissionStatus[],
        kind,
        fromIso: dayBoundsIso(fromDay, 'start'),
        toIso: dayBoundsIso(toDay, 'end'),
      }),
    // Leads arrive and retry on their own; a stale log hides a failure.
    refetchInterval: 30_000,
  });
  const detail = useQuery({
    queryKey: ['lead-submission', selectedId],
    queryFn: () => getLeadSubmission(selectedId!),
    enabled: selectedId !== null,
  });

  const resend = useMutation({
    mutationFn: (id: string) => resendLead(id),
    onSuccess: (outcome) => {
      void queryClient.invalidateQueries({ queryKey: ['lead-log'] });
      void queryClient.invalidateQueries({ queryKey: ['lead-submission', outcome.submissionId] });
      if (outcome.status === 'submitted') toast.success(`Resent — ${systemName(detail.data?.submission.provider ?? null)} accepted it.`);
      else toast.warning(`Resent, but it is ${STATUS_LABELS[outcome.status].toLowerCase()}. The attempts below say why.`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ⚠️ The send result is shown INLINE in the lead's panel, success or failure —
  // never only as a toast, because "accepted is not received" needs reading.
  const [assignResult, setAssignResult] = useState<
    { id: string; routeName: string; outcome: RelayOutcome | null; error: string | null } | null
  >(null);
  const assign = useMutation({
    mutationFn: (v: { id: string; routeId: string; routeName: string; provider: LeadProvider | null }) =>
      assignLead(v.id, v.routeId, v.provider),
    onSuccess: (outcome, v) => {
      setAssignResult({ id: v.id, routeName: v.routeName, outcome, error: null });
      void queryClient.invalidateQueries({ queryKey: ['lead-log'] });
      void queryClient.invalidateQueries({ queryKey: ['lead-submission', outcome.submissionId] });
      void queryClient.invalidateQueries({ queryKey: ['lead-summary'] });
    },
    // Inline too: "did it go?" must be answered where the person clicked.
    onError: (err: Error, v) => {
      setAssignResult({ id: v.id, routeName: v.routeName, outcome: null, error: err.message });
      void queryClient.invalidateQueries({ queryKey: ['lead-submission', v.id] });
    },
  });

  const markKeyed = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => markLeadKeyedManually(id, note),
    onSuccess: (submission) => {
      void queryClient.invalidateQueries({ queryKey: ['lead-log'] });
      void queryClient.invalidateQueries({ queryKey: ['lead-submission', submission.id] });
      void queryClient.invalidateQueries({ queryKey: ['lead-summary'] });
      toast.success(
        submission.routeId === null
          ? 'Marked as keyed in by hand. It has left the Unassigned queue.'
          : 'Marked as keyed in by hand. It no longer counts as a failure.',
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Saving an edit sends NOTHING — the lead stays failed until Resend is pressed.
  const editAnswers = useMutation({
    mutationFn: ({ id, fields }: { id: string; fields: Parameters<typeof editLeadInput>[1] }) => editLeadInput(id, fields),
    onSuccess: (submission) => {
      void queryClient.invalidateQueries({ queryKey: ['lead-submission', submission.id] });
      toast.success('Saved. Nothing has been sent — Resend to apply it.');
    },
  });

  const storeOptions = useMemo(
    () => [{ value: '', label: 'All routes' }, ...routes.map((r) => ({ value: r.id, label: r.name }))],
    [routes],
  );
  const statusOptions = useMemo(() => ALL_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] })), []);

  const rows = log.data?.rows ?? [];
  const unassigned = log.data?.unassigned ?? null;
  const showingOnlyUnassigned = statuses.length === 1 && statuses[0] === 'unassigned';
  /** The queue, filtered — reaching back to the oldest waiting lead if the window would hide it. */
  const showUnassigned = () => {
    const oldestDay = unassigned?.oldestReceivedAt ? unassigned.oldestReceivedAt.slice(0, 10) : null;
    const windowStart = dayBoundsIso(fromDay, 'start') ?? new Date(Date.now() - 30 * 86_400_000).toISOString();
    const reachBack = unassigned?.oldestReceivedAt && unassigned.oldestReceivedAt < windowStart ? oldestDay : null;
    update({ status: 'unassigned', kind: null, store: null, ...(reachBack ? { from: reachBack, to: null } : {}) });
  };

  return (
    <div>
      <PageHeader
        title="Lead log"
        description="Every Meta lead the relay received, newest first. An unassigned lead is assigned and sent from its detail; a failed lead can be keyed into the dealer's or club's system by hand, or resent."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="w-56">
          <SelectField
            value={routeId}
            onValueChange={(v) => update({ store: v })}
            options={storeOptions}
            aria-label="Route"
          />
        </div>
        <MultiSelectFilter
          label="Statuses"
          options={statusOptions}
          selected={statuses}
          onChange={(next) => update({ status: next.length === 0 ? 'all' : next.join(',') })}
        />
        <div className="w-44">
          <SelectField
            value={kind}
            onValueChange={(v) => update({ kind: v === 'live' ? null : v })}
            options={[
              { value: 'live', label: 'Real leads' },
              { value: 'test', label: 'Test leads' },
              { value: 'all', label: 'All' },
            ]}
            aria-label="Lead kind"
          />
        </div>
        <DateRangePicker from={fromDay} to={toDay} onChange={(from, to) => update({ from: from ?? null, to: to ?? null })} />
        {!fromDay && <span className="text-sm text-muted-foreground">Last 30 days</span>}
      </div>

      {unassigned && unassigned.count > 0 && (
        // ⚠️ Shown WITHOUT any filter applied: an unassigned lead has been sent
        // nowhere, and nobody should have to go looking to find that out.
        <div
          className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="unassigned-queue"
        >
          <span className="font-medium">
            {unassigned.count} unassigned lead{unassigned.count === 1 ? '' : 's'} waiting — not sent to any dealer until
            someone deals with {unassigned.count === 1 ? 'it' : 'them'}.
          </span>
          {!showingOnlyUnassigned && (
            <button type="button" className="underline" onClick={showUnassigned}>
              Show unassigned
            </button>
          )}
        </div>
      )}

      {log.data?.truncated && (
        <p className="mb-2 text-sm text-amber-700">
          Showing the newest {log.data.limit} records in this window — narrow the dates or pick a store to see older ones.
        </p>
      )}

      {log.isLoading ? (
        <Spinner />
      ) : log.error ? (
        <p className="text-sm text-red-700">{(log.error as Error).message}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No leads match these filters.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Received</TableHead>
              <TableHead>{destinationHeading(rows.map((r) => r.provider))}</TableHead>
              <TableHead>Lead</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Attempts</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer"
                onClick={() => update({ submission: row.id })}
                data-testid="lead-log-row"
              >
                <TableCell className="whitespace-nowrap">{formatExactTimestamp(row.receivedAt)}</TableCell>
                <TableCell>
                  {row.storeName ?? (
                    <span className="text-muted-foreground">{row.status === 'unassigned' ? 'Unassigned' : 'No route'}</span>
                  )}
                </TableCell>
                <TableCell>
                  {row.leadName}
                  {row.kind === 'test' && <Badge variant="outline" className="ml-2">{row.testOrigin ? 'Test (direct, not sent to CRM)' : 'Test'}</Badge>}
                </TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-1">
                    <Badge variant={statusBadgeVariant(row.status)}>{STATUS_LABELS[row.status]}</Badge>
                    {willSubmitDespiteTestMode(row) && (
                      // ⚠️ Visible on the row, not only in the detail: a paused
                      // store sending a lead must not be a surprise.
                      <Badge variant="warning" title="Queued before the route was paused — test mode stops new leads only.">
                        Will still submit — route in test mode
                      </Badge>
                    )}
                  </div>
                  {row.status === 'failed' && row.failureReason && (
                    <div className="mt-1 text-xs text-muted-foreground">{failureLabel(row.failureReason, row.provider)}</div>
                  )}
                  {row.status === 'unassigned' && row.unassignedReason && (
                    <div className="mt-1 text-xs text-muted-foreground">{UNASSIGNED_REASON_LABELS[row.unassignedReason]}</div>
                  )}
                </TableCell>
                <TableCell className="text-right">{row.attemptCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={selectedId !== null} onOpenChange={(open) => !open && update({ submission: null })}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {detail.data
                ? `${detail.data.submission.input.first_name ?? ''} ${detail.data.submission.input.last_name ?? ''}`.trim() || 'Lead'
                : 'Lead'}
            </DialogTitle>
          </DialogHeader>
          {detail.isLoading ? (
            <Spinner />
          ) : detail.error ? (
            <p className="text-sm text-red-700">{(detail.error as Error).message}</p>
          ) : detail.data ? (
            <LeadSubmissionPanel
              detail={detail.data}
              onResend={() => resend.mutate(detail.data!.submission.id)}
              resendPending={resend.isPending}
              onMarkKeyed={(note) => markKeyed.mutate({ id: detail.data!.submission.id, note })}
              markKeyedPending={markKeyed.isPending}
              onEditAnswers={async (fields) => {
                await editAnswers.mutateAsync({ id: detail.data!.submission.id, fields });
              }}
              onAssign={isAdmin ? (routeId, routeName, provider) => assign.mutate({ id: detail.data!.submission.id, routeId, routeName, provider }) : undefined}
              assignPending={assign.isPending}
              assignResult={assignResult && assignResult.id === detail.data.submission.id ? assignResult : null}
              relayConfigured={status?.relayConfigured ?? false}
              isAdmin={isAdmin}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
